<script setup lang="ts">
import { api } from '@/api';
import {
  TrackItTaskResultForEvaluation,
  TrackItTaskWorkItemDto,
  TrackItTaskWorkResultDto,
  TrackItTaskWorkStepDto,
} from '@/api/generator';
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import Attachments from '@/components/forms/Attachments/Attachments.vue';
import Textarea from '@/components/forms/Textarea/Textarea.vue';
import { useFormMode } from '@/components/forms/useFormMode';
import NamedGroup from '@/components/NamedGroup/NamedGroup.vue';
import RTSystemTile from '@/components/RTSystemTile/RTSystemTile.vue';
import Button from '@/components/buttons/Button.vue';
import { useToggle } from '@vueuse/core';
import { ref, toRef } from 'vue';
import { useTrackItTask } from '../utils/useTrackItTask';
import { useJobOccurrence } from '../utils/useJobOccurrence';
import JobExecutionWorkSteps from './JobExecutionWorkSteps.vue';
import TrackItWorkItemPanel from './workItems/TrackItWorkItemPanel.vue';
import TrackItMeasurementPickerDialog from './workItems/TrackItMeasurementPickerDialog.vue';
import TrackItWorkItemMeasurementPreviewDialog from './workItems/TrackItWorkItemMeasurementPreviewDialog.vue';

const props = defineProps<{
  workitem: TrackItTaskWorkItemDto;
}>();

const mode = useFormMode();

const {
  jobOccurrence,
  reload,
  areAllWorkstepsSkipped,
  isSkippAllPossible,
  toggleSkipAllWorksteps,
} = useJobOccurrence();

const { supportSystemGroups, numGroups } = useTrackItTask(toRef(() => props.workitem));

const [pickerOpen, togglePicker] = useToggle(false);

const pickerWorkStep = ref<TrackItTaskWorkStepDto | undefined>(undefined);

const [importPreviewOpen, toggleImportPreview] = useToggle(false);

function openPicker(workStep: TrackItTaskWorkStepDto) {
  pickerWorkStep.value = workStep;
  togglePicker(true);
}

async function onPickerApply(payload: {
  trackItMeasurementId: number;
  additionalResults: TrackItTaskResultForEvaluation[];
  result: TrackItTaskWorkResultDto;
}) {
  if (!jobOccurrence.value.id || !pickerWorkStep.value) {
    return;
  }

  const { success } = await api.trackItImportApi.importTrackItMeasurementForWorkStep({
    importTrackItMeasurementForWorkStepDto: {
      resultId: payload.result.id,
      trackItMeasurementId: payload.trackItMeasurementId,
      jobOccurrenceId: jobOccurrence.value.id,
      workStepId: pickerWorkStep.value.id,
      additionalResults: payload.additionalResults,
    },
  });

  if (success) {
    await reload();
  }
}
</script>

<template>
  <TrackItMeasurementPickerDialog
    v-if="pickerOpen && pickerWorkStep"
    v-model="pickerOpen"
    :workStep="pickerWorkStep"
    @apply="onPickerApply"
  />

  <TrackItWorkItemMeasurementPreviewDialog
    v-if="importPreviewOpen"
    v-model="importPreviewOpen"
    :workitem="workitem"
  />

  <v-row
    v-if="
      numGroups ||
      workitem.instruction?.textAreaContent ||
      workitem.instruction?.attachments?.length
    "
  >
    <v-col v-if="numGroups" cols="8">
      <FormLabel class="mb-2" :label="$t('DosimetryAndQaEquipment')">
        <NamedGroup :groups="supportSystemGroups">
          <template #default="group">
            <v-row dense class="mt-2">
              <v-col
                v-for="supportSystem in group.items"
                :key="supportSystem.id"
                cols="12"
                lg="6"
                xxl="4"
                class="mb-4"
              >
                <RTSystemTile class="w-100 h-100" :item="supportSystem" />
              </v-col>
            </v-row>
          </template>
        </NamedGroup>
      </FormLabel>
    </v-col>

    <v-col
      v-if="workitem.instruction?.textAreaContent || workitem.instruction?.attachments?.length"
      cols="4"
    >
      <FormLabel :label="$t('Instruction_one')">
        <Textarea
          v-if="workitem.instruction?.textAreaContent"
          :modelValue="workitem.instruction.textAreaContent"
          mode="details"
        />
        <v-divider
          v-if="workitem.instruction?.textAreaContent && workitem.instruction?.attachments?.length"
          color="primary-lighten-4"
          class="mt-1 mb-1"
          thickness="1"
        ></v-divider>
        <Attachments
          v-if="workitem.instruction?.attachments?.length"
          :modelValue="workitem.instruction?.attachments"
          mode="details"
        />
      </FormLabel>
    </v-col>
  </v-row>

  <v-row v-if="mode === 'execution'">
    <v-col class="d-flex justify-end align-center ga-4">
      <v-checkbox
        v-if="(workitem.workSteps?.length ?? 0) > 1 && isSkippAllPossible(workitem)"
        :label="$t('JobExecutionSkipAll')"
        density="compact"
        hideDetails
        class="mr-2"
        :modelValue="areAllWorkstepsSkipped(workitem)"
        @click.stop="toggleSkipAllWorksteps(workitem, !areAllWorkstepsSkipped(workitem))"
      />

      <Button
        variant="secondary"
        size="small"
        label="Import Current Measurements"
        @click="toggleImportPreview(true)"
      />
    </v-col>
  </v-row>

  <v-row>
    <v-col>
      <JobExecutionWorkSteps class="mt-4" :workSteps="workitem.workSteps || undefined">
        <template #actions="{ step }">
          <Button
            v-if="mode === 'execution'"
            variant="secondary"
            :disabled="!step.isExecutable"
            class="mr-3"
            size="small"
            :label="$t('Import')"
            @click.stop.prevent="openPicker(step)"
          />
        </template>

        <template #workitem="{ step }">
          <TrackItWorkItemPanel :step="step" />
        </template>
      </JobExecutionWorkSteps>
    </v-col>
  </v-row>
</template>
